import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Alert, Badge, Button, EmptyState, ErrorState, SkeletonRow, Table,
} from '@parsina/framework';
import { ChevronLeft, Earth, Plus } from 'lucide-react';

import { useTranslation, MIRROR_ICON } from '../../i18n';
import { gql } from '../../lib/api';
import { useShortcut } from '../../lib/shortcuts';
import {
  CAPABILITY_FIELDS, FALLBACK_COUNTRY, countryPath, fieldSpec,
} from '../../constants/countryProfile';
import { capabilityValues, ceilingOf } from '../../lib/countryProfileView';
import { COUNTRY_PROFILES_QUERY } from './queries';
import {
  ColumnValue, CountryCode, EditorLabel, countryErrorMessage, useCountryName,
} from './shared';
import CreateCountryDialog from './CreateCountryDialog';

/**
 * «پروفایل‌ها» — every stored row, `*` first (the service's order). A row is a
 * jurisdiction's rules; it opens the country's record.
 *
 * The capability cell is a COUNT, not eight chips: the record is where they are
 * read one by one, with the ceiling beside each. What the list must not hide
 * is a capability this deployment switches off whatever the row says.
 *
 * «افزودن کشور» (PM-0243) gives a country without a row its first one, as a
 * copy; it is offered only where the editor answers (`editorEnabled` — absent,
 * never disabled, where it cannot work), and the new country's record opens.
 */
export default function ProfilesView() {
  const { t, formatDate, formatNumber } = useTranslation();
  const navigate = useNavigate();
  const countryName = useCountryName();

  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [creating, setCreating] = React.useState(false);

  const load = React.useCallback(() => {
    setLoading(true);
    setError(null);
    gql(COUNTRY_PROFILES_QUERY)
      .then((d) => {
        if (!d?.countryProfiles) throw new Error(t('countries.list.error'));
        setData(d.countryProfiles);
      })
      .catch((e) => setError(countryErrorMessage(e, t)))
      .finally(() => setLoading(false));
  }, [t]);

  React.useEffect(() => { load(); }, [load]);

  const deployment = data?.deployment;
  const rows = data?.profiles || [];
  const fallback = rows.find((r) => r.country === FALLBACK_COUNTRY) || null;
  const total = CAPABILITY_FIELDS.length;
  // false = the editor contract does not answer here; null = could not tell —
  // then the action is offered and the write reports what is wrong.
  const canCreate = !!data && data.editorEnabled !== false;

  useShortcut('n', () => setCreating(true), {
    label: t('countries.create.title'),
    group: t('nav.items.countries'),
    enabled: canCreate && !creating,
  });

  const columns = [
    {
      key: 'country',
      header: t('countries.list.columns.country'),
      render: (r) => (
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate font-semibold text-foreground">{countryName(r.country)}</span>
          <CountryCode code={r.country} />
        </div>
      ),
    },
    {
      key: 'language',
      header: t('countries.fields.defaultLanguage'),
      render: (r) => <ColumnValue spec={fieldSpec('defaultLanguage')} row={r} fallback={fallback} />,
    },
    {
      key: 'currency',
      header: t('countries.fields.defaultCurrency'),
      render: (r) => <ColumnValue spec={fieldSpec('defaultCurrency')} row={r} fallback={fallback} />,
    },
    {
      key: 'timezone',
      header: t('countries.fields.defaultTimezone'),
      render: (r) => <ColumnValue spec={fieldSpec('defaultTimezone')} row={r} fallback={fallback} />,
    },
    {
      key: 'capabilities',
      header: t('countries.list.columns.capabilities'),
      render: (r) => {
        const on = CAPABILITY_FIELDS.filter((s) => capabilityValues(r[s.field]).length);
        const off = CAPABILITY_FIELDS.filter((s) => {
          const { offHere } = ceilingOf(deployment, s);
          return capabilityValues(r[s.field]).some((v) => offHere.includes(v));
        });
        return (
          <span className="flex flex-wrap items-center gap-x-2" data-testid={`countries-capabilities-${r.country}`}>
            <span
              className="tabular-nums"
              title={on.map((s) => t(`countries.fields.${s.field}`)).join(' · ') || undefined}
            >
              {t('countries.list.capabilitiesOn', { on: formatNumber(on.length), total: formatNumber(total) })}
            </span>
            {off.length ? (
              // A status, not an error: the warning chip, never red.
              <Badge variant="warning" title={t('countries.ceiling.offHereHint')}>
                {t('countries.list.offHere', { count: off.length })}
              </Badge>
            ) : null}
          </span>
        );
      },
    },
    {
      key: 'updated',
      header: t('countries.list.columns.updated'),
      render: (r) => (
        <span className="flex items-center gap-2">
          <span>{r.updatedAt ? formatDate(r.updatedAt) : t('common.value.none')}</span>
          <EditorLabel by={r.updatedBy} />
        </span>
      ),
    },
    {
      key: 'open',
      header: <span className="sr-only">{t('countries.list.columns.open')}</span>,
      align: 'end',
      width: '6rem',
      render: (r) => (
        <Button
          asChild
          variant="ghost"
          size="sm"
          // The row's own handler would fire too and navigate twice.
          onClick={(e) => e.stopPropagation()}
          data-testid={`countries-row-${r.country}-open`}
        >
          <Link to={countryPath(r.country)} aria-label={t('countries.list.open', { name: countryName(r.country) })}>
            {t('common.actions.details')}
            <ChevronLeft size={14} aria-hidden style={MIRROR_ICON} />
          </Link>
        </Button>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {canCreate ? (
        <div className="flex justify-end">
          <Button onClick={() => setCreating(true)} data-testid="countries-create">
            <Plus size={16} aria-hidden />
            {t('countries.create.action')}
          </Button>
        </div>
      ) : null}

      {deployment?.market === 'intl' ? (
        <Alert variant="warning" title={t('countries.ceiling.intlTitle')} data-testid="countries-alert-intl">
          {t('countries.ceiling.intlBody')}
        </Alert>
      ) : null}

      <div className="rounded-lg border border-border bg-card">
        {loading ? (
          <div data-testid="countries-loading" data-state="loading" role="status" aria-busy="true" aria-label={t('countries.list.loading')}>
            <table className="w-full">
              <tbody>
                {Array.from({ length: 3 }).map((_, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <SkeletonRow key={i} cols={7} />
                ))}
              </tbody>
            </table>
          </div>
        ) : error ? (
          <ErrorState
            title={t('countries.list.error')}
            description={error}
            onRetry={load}
            retryLabel={t('common.actions.retry')}
            data-testid="countries-error"
          />
        ) : (
          <div data-state={rows.length ? 'content' : 'empty'}>
            <Table
              dense
              ariaLabel={t('countries.list.aria')}
              columns={columns}
              rows={rows}
              rowKey={(r) => r.country}
              onRowActivate={(r) => navigate(countryPath(r.country))}
              rowProps={(r) => ({ 'data-testid': `countries-row-${r.country}` })}
              empty={(
                <EmptyState
                  icon={<Earth size={44} strokeWidth={1.5} aria-hidden />}
                  title={t('countries.list.empty.title')}
                  description={t('countries.list.empty.desc')}
                  data-testid="countries-empty"
                />
              )}
              data-testid="countries-table"
            />
          </div>
        )}
      </div>

      {canCreate ? (
        <CreateCountryDialog
          open={creating}
          onOpenChange={setCreating}
          existing={rows}
          onCreated={(code) => navigate(countryPath(code))}
        />
      ) : null}
    </div>
  );
}
